import { useWatch } from 'react-hook-form';
import { type ReturnDate } from '@knack/asterisk-react/dist/src/components/date-time-picker/components/DateTimeInput/DateTimeInput';
import { type DateRangeWithErrors } from '@knack/asterisk-react/dist/src/components/date-time-picker/DateTimePicker';
import { DateTime } from 'luxon';

import { type DateTimeField } from '@/types/schema/fields/DateTimeField';
import { useFieldNameConcatenate } from '@/utils/forms/useFieldNameConcatenate';
import { KnackDatePickerRepeatOptions } from '@/components/knack-date-picker/components/KnackDatePickerRepeatOptions';
import { DatePickerFormWrapper } from '@/components/knack-date-picker/form/DatePickerFormWrapper';
import { type KnackDatePickerSchema } from '@/components/knack-date-picker/knackDatePickerChildFormSchema';
import {
  getKnackDatePickerFormatFromField,
  getLuxonDateFormatFromFieldFormat
} from '@/components/knack-date-picker/transformers/DateTimeFormatToLuxonFormat';
import { getNormalizedDefaultTimeFormat } from '@/components/knack-date-picker/utils';

type KnackDatePickerChildFormProps = {
  formPath?: string;
  dateTimeField: DateTimeField;
  isRepeatDisabled?: boolean;
  value: KnackDatePickerSchema;
};

export function getKnackDateInitialValue(dateTimeField: DateTimeField) {
  const fieldFormat = dateTimeField.format;
  let dateTime: Date | undefined;
  let dateValue: Date | undefined;
  let timeValue: Date | undefined;

  if (fieldFormat.date_format !== 'Ignore Date') {
    if (fieldFormat.default_type === 'current') {
      dateValue = new Date();
    } else if (fieldFormat.default_type === 'date' && fieldFormat.default_date) {
      const luxonDateFormat = getLuxonDateFormatFromFieldFormat(fieldFormat.date_format);

      const parsedDate = DateTime.fromFormat(
        fieldFormat.default_date,
        luxonDateFormat || 'MM/dd/yyyy'
      );
      if (parsedDate.isValid) {
        dateValue = parsedDate.toJSDate();
      }
    }

    if (fieldFormat.time_format === 'Ignore Time') {
      dateTime = dateValue;
    }
  }

  if (fieldFormat.time_format !== 'Ignore Time') {
    if (fieldFormat.time_type === 'current') {
      timeValue = new Date();
    } else if (fieldFormat.time_type === 'time' && fieldFormat.default_time) {
      const luxonTimeFormat = getNormalizedDefaultTimeFormat(fieldFormat.default_time, fieldFormat);

      const parsedTime = DateTime.fromFormat(fieldFormat.default_time, luxonTimeFormat);
      if (parsedTime.isValid) {
        timeValue = parsedTime.toJSDate();
      }
    }

    if (fieldFormat.date_format === 'Ignore Date') {
      dateTime = timeValue;
    }
  }

  if (dateValue && timeValue) {
    dateTime = new Date(dateValue);
    dateTime.setHours(timeValue.getHours());
    dateTime.setMinutes(timeValue.getMinutes());
    dateTime.setSeconds(timeValue.getSeconds());
    dateTime.setMilliseconds(0);
  }

  return {
    dateTime,
    dateValue,
    timeValue
  };
}

export function KnackDatePickerChildForm(props: KnackDatePickerChildFormProps) {
  const { formPath, dateTimeField, value, isRepeatDisabled = false } = props;

  const getFieldKey = useFieldNameConcatenate<KnackDatePickerSchema>(formPath);

  const allDay = useWatch({ name: getFieldKey('all_day') });

  const calendarMode = dateTimeField.format.calendar ? 'range' : 'single';

  const datePickerFormat = getKnackDatePickerFormatFromField(dateTimeField.format);

  const initialValue = getKnackDateInitialValue(dateTimeField);

  const datePickerValue = value.date || initialValue.dateTime;

  return (
    <div className="w-full">
      <DatePickerFormWrapper
        value={datePickerValue as ReturnDate | DateRangeWithErrors} // TODO: needs better typings?
        mode={calendarMode}
        name={getFieldKey('date')}
        format={{
          date: datePickerFormat.date,
          time: allDay === true ? undefined : datePickerFormat.time
        }}
        dateInitialValue={initialValue.dateValue}
        timeInitialValue={initialValue.timeValue}
      />
      {!isRepeatDisabled && (
        <KnackDatePickerRepeatOptions
          formPath={formPath}
          mode={calendarMode}
          format={datePickerFormat}
        />
      )}
    </div>
  );
}
